import qs from 'qs';
import { AxiosResponse } from 'axios';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import { getActiveLanguageCode } from 'services/i18nService';
import { EPAY_PRODUCT_TYPES } from 'enum/payments';
import { MONTH_AS_DAYS } from 'enum/timeFormat';
import { calcPricePerMonth } from 'utils/helpers';
import { User } from 'stores/UserStore/UserStore.types';
import TracingService from 'services/TracingService';


export type Method = {
  processorId: number;
  type: string;
  hasOneClick?: boolean;
}

export type Category = {
  methods: Array<Method>;
  type: string;
}

export type Currency = {
  name: string;
  symbol: string;
}

export interface Product {
  amount: number;
  categories: Array<Category>;
  currency: Currency;
  details: {
    bonus: number;
    recurringPeriod: number;
  }
  id: number;
  quantity: number;
  type: EPAY_PRODUCT_TYPES;
}
export type Products = Array<Product>

export interface Membership extends Omit<Product, 'currency' | 'categories' | 'details' | 'quantity' | 'type'>{
  currency: Currency['symbol'];
  recurringPeriod: Product['details']['recurringPeriod'];
  price: string | number;
  months: number;
  item: Product;
}

export type ProductsByProcessor = Record<string, {
  processorId: number;
  products: Products;
  hasOneClick?: boolean;
}>

export type CryptoCurrency = {
  currency: string;
  description: string;
}

export type AvailableCrypto = {
  value: CryptoCurrency['currency'];
  label: CryptoCurrency['description'];
  cryptoData: CryptoCurrency;
}

declare global {
  interface Window {
    abt?: {
      pricing?: {
        extraRequestParams?: Promise<Record<string, string>> | Record<string, string>;
        computeProducts?: (prods: Products) => Products;
      }
    }
  }
}

const DISCOUNT_ELIGIBLE = 'COMEBACK2022';
const EPAY_SERVER = ConfigurationService.get(ConfigurationKeys.EPAY_SERVER);
const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);
const EPAY_TENANT = ConfigurationService.get(ConfigurationKeys.EPAY_TENANT);

const EPAY_API_CHECKOUT = `${EPAY_SERVER}/rest/v1/checkout/${EPAY_TENANT}`;
const EPAY_API_DECLINE = `${EPAY_SERVER}/rest/v1/checkout/decline/${EPAY_TENANT}`;
const EPAY_API_PAYMENT_DETAILS = `${EPAY_SERVER}/rest/v1/paymentTypeDetails/${EPAY_TENANT}`;
const EPAY_API_PAYMENT_STATUS = `${EPAY_SERVER}/rest/v2/return/paymentRequestStatus/${EPAY_TENANT}`;

const MAX_ABT_CONFIG_WAIT_DURATION = 100;
const API = {
  GET_PRODUCTS: `${EPAY_API_CHECKOUT}/{language}/{type}`,
  GET_PROCESSOR_DATA: `${EPAY_API_CHECKOUT}/{language}/{username}/{priceId}/{processorId}`,
  GUEST_ACCOUNT: `${BASE_URL}/checkout`,
  GET_PRODUCTS_FOR_GOLD_MODAL:
    `${EPAY_API_CHECKOUT}/{language}/TOKENS-MEMBERSHIP?siteSubcat=cam4.com`,
  GET_ALTERNATIVE_PAYMENT_METHODS:
    `${EPAY_API_DECLINE}/{language}/{userName}/{purchaseId}?accessHash={accessHash}`,
  GET_CRYPTOCURRENCIES: `${EPAY_API_PAYMENT_DETAILS}/{processorId}`,
  GET_CRYPTOCURRENCY_PAYMENT_CONFIRMATION:
    `${EPAY_API_PAYMENT_STATUS}/{processorId}/{purchaseId}/{hash}`,
};

class EpayService {
  products: ProductsByProcessor = {};
  memberships: Array<Membership> = [];
  selectedMembership: Membership = null;
  username = '';
  windowName = 'Checkout Page';
  checkoutWindow: Window = null;
  optionalParams: Record<string, string>;
  availableCryptoCurrencies: Array<AvailableCrypto> = null;
  checkoutForm: HTMLFormElement;

  async getProducts(
    username: string,
    accessHash: string,
    type: EPAY_PRODUCT_TYPES,
    optionalQueryParams: Record<string, string> = null
  ): Promise<ProductsByProcessor | AxiosResponse<Products>> {
    const activeLanguageCode = getActiveLanguageCode();
    const { products } = this;

    if (products.length && username === this.username) {
      return products;
    }

    const url = ApiService.substitute(API.GET_PRODUCTS, {
      language: activeLanguageCode || 'en',
      type: type || EPAY_PRODUCT_TYPES.TOKENS,
    });

    const queryParams: Record<string, string> = { siteSubcat: 'cam4.com' };
    if (username) {
      queryParams.userName = username;
      queryParams.accessHash = accessHash;
      this.username = username;
    }

    if (optionalQueryParams && optionalQueryParams.pvalue === DISCOUNT_ELIGIBLE) {
      queryParams.pvalue = DISCOUNT_ELIGIBLE;
    }

    await this.processOptionalParams(queryParams);

    const headers = TracingService.generateHeaders();

    return ApiService.get(encodeURI(url), {
      params: queryParams,
      withCredentials: false,
      headers,
    });
  }

  private async processOptionalParams(params: Record<string, string>) {
    const _optionalParams = window.abt?.pricing?.extraRequestParams;
    if (_optionalParams && _optionalParams.then) {
      const timeout = new Promise<never>((resolve) => setTimeout(resolve, MAX_ABT_CONFIG_WAIT_DURATION));
      this.optionalParams = await Promise.race([_optionalParams, timeout]);
    } else {
      this.optionalParams = _optionalParams as Record<string, string>;
    }
    if (this.optionalParams) {
      Object.keys(this.optionalParams).forEach((key) => {
        if (!params[key]) {
          params[key] = this.optionalParams[key];
        }
      });
    }
  }

  /**
   * Fetch details for cryptocurrency packages
   */
  getCryptoCurrencies(
    selectedOption: {
      value: CryptoCurrency['currency'];
      processorId: Method['processorId'];
    },
    user: User
  ): Promise<AxiosResponse> {
    const { products: { CR } } = this;
    const DEFAULT_PRICE_ID = 506;
    const priceId = (CR.products.length && CR.products[0].id) || DEFAULT_PRICE_ID;
    const url = ApiService.substitute(
      API.GET_CRYPTOCURRENCIES,
      { processorId: selectedOption.processorId.toString() }
    );

    const queryParams = {
      paymentMethod: selectedOption.value,
      productType: 'TOKENS',
      priceId,
      siteSubcat: 'cam4.com',
      userName: user.username || '',
      accessHash: user.accessHash || '',
    };

    return ApiService.get(encodeURI(url), {
      params: queryParams,
      withCredentials: false,
    });
  }

  /**
   * Check status of cryptocurrency payment
   */
  getCryptoCurrencyPaymentConfirmation(
    processorId: Method['processorId'],
    purchaseId: string,
    hash: string
  ): Promise<AxiosResponse> {
    const url = ApiService.substitute(API.GET_CRYPTOCURRENCY_PAYMENT_CONFIRMATION, {
      processorId: processorId.toString(),
      purchaseId: purchaseId.toString(),
      hash,
    });

    return ApiService.get(encodeURI(url), { withCredentials: false });
  }

  /**
   * Send API call to get products for register gold user
   */
  getProductsForGoldenRegistration(): Promise<AxiosResponse> {
    const activeLanguageCode: string = getActiveLanguageCode();
    const url = ApiService.substitute(
      API.GET_PRODUCTS_FOR_GOLD_MODAL,
      { language: activeLanguageCode }
    );

    return ApiService.get(encodeURI(url), { withCredentials: false });
  }

  /**
   * Send API call to get altername payment methods
   */
  getAlternativePaymentMethods(
    purchaseId = '',
    userName = '',
    accessHash = ''
  ): Promise<AxiosResponse> {
    const activeLanguageCode = getActiveLanguageCode();
    const url = ApiService.substitute(API.GET_ALTERNATIVE_PAYMENT_METHODS, {
      language: activeLanguageCode,
      accessHash,
      userName,
      purchaseId,
    });

    return ApiService.get(encodeURI(url), { withCredentials: false });
  }

  /**
   * Sets the property products by parsing the provided products from backend
   */
  setProducts(prods: Products): void {
    const products = window.abt?.pricing?.computeProducts?.(prods) || prods;

    this.products = products.reduce((acc, item) => {
      const types = item.categories.reduce((arr, category) => {
        const categories = category.methods.map((method) => {
          const { type, processorId, hasOneClick } = method;

          return { method: type, processorId, hasOneClick };
        });

        return arr.concat(categories);
      }, []);

      types.forEach((type) => {
        if (acc[type.method]) {
          acc[type.method].products.push(item);
        } else {
          acc[type.method] = {
            products: [item],
            processorId: type.processorId,
            hasOneClick: type.hasOneClick,
          };
        }
      });

      return acc;
    }, {} as ProductsByProcessor);
  }

  /**
   * Pass cryptocurrency details from response
   */
  setCryptoCurrencies(cryptos: Array<CryptoCurrency>): void {
    this.availableCryptoCurrencies = cryptos.map((crypto) => {
      return {
        value: crypto.currency,
        label: crypto.description,
        cryptoData: { ...crypto },
      };
    });
  }

  /**
   * Parse membership products from backend
   */
  setMemberships(products: Products): void {
    this.memberships = products.map((product) => {
      const { recurringPeriod } = product.details;

      return {
        id: product.id,
        currency: product.currency.symbol,
        recurringPeriod: product.details.recurringPeriod,
        amount: product.amount,
        price: calcPricePerMonth(product),
        months: parseInt(String(recurringPeriod), 10) / MONTH_AS_DAYS,
        item: product,
      };
    });
  }

  /**
   * Take membership as argument and mark it as selected
   */
  setSelectedMembership(membership: Membership) {
    this.selectedMembership = membership;
  }

  /**
   * Send API call to create guest account
   */
  createGuestAccount(): Promise<AxiosResponse> {
    return ApiService.post(
      API.GUEST_ACCOUNT, {
        data: qs.stringify({ isGuestRegister: true }),
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
      }
    );
  }

  /**
   * Get payment processor data for input fields that are needed for form in payment window
   */
  async getPaymentProcessorData(
    processorId: Method['processorId'],
    priceId: number,
    username: string,
    options: Record<string, string>
  ): Promise<AxiosResponse> {
    const activeLanguageCode = getActiveLanguageCode();

    const url = ApiService.substitute(API.GET_PROCESSOR_DATA, {
      language: activeLanguageCode,
      username: username || '',
      processorId: processorId.toString(),
      priceId: priceId.toString(),
    });

    await this.processOptionalParams(options);

    return ApiService.post(
      encodeURI(url),
      {
        data: JSON.stringify(options),
        headers: {
          ...TracingService.generateHeaders(),
          'content-type': 'application/json; charset=utf-8',
        },
        withCredentials: false,
      },
    );
  }

  /**
   * Prepare form that will be submitted in new window
   */
  prepareForm(data: {
    parameters: Array<string>;
    type: string;
    url: string;
  }): void {
    const { parameters, type, url } = data;
    const form = document.createElement('form');
    form.setAttribute('method', type || 'POST');
    form.setAttribute('action', url);
    form.setAttribute('id', 'checkoutForm');
    form.setAttribute('style', 'display: none');
    if (parameters) {
      Object.entries(parameters).forEach((parameter) => {
        const input = document.createElement('input');
        input.setAttribute('name', parameter[0]);
        input.setAttribute('value', parameter[1]);
        form.appendChild(input);
      });
    } else {
      const newUrl = new URL(url);
      const params = qs.parse(newUrl.search, { ignoreQueryPrefix: true });

      for (const [key, value] of Object.entries(params)) {
        const field = document.createElement('input');
        field.setAttribute('name', key);
        field.setAttribute('value', value as string);
        form.appendChild(field);
      }
    }

    if (this.optionalParams) {
      Object.keys(this.optionalParams).forEach((key) => {
        const field = document.createElement('input');
        field.setAttribute('name', key);
        field.setAttribute('value', this.optionalParams[key]);
        form.appendChild(field);
      });
    }

    this.checkoutForm = form;
  }

  /**
   * It writes and submit form in new window
   */
  submitForm(): void {
    this.checkoutWindow.document.write(`<div>${this.checkoutForm.outerHTML}</div>`);
    (this.checkoutWindow.document.getElementById('checkoutForm') as HTMLFormElement).submit();
  }

  /**
   * It should close checkout window and set it's value to null
   */
  closePaymentProcessorWindow(): void {
    this.checkoutWindow?.close?.();
    this.checkoutWindow = null;
  }

  /**
   * Open new window based on processor id
   */
  openPaymentProcessorWindow(processorId: Method['processorId']): void {
    const name = this.windowName;

    const windowDimensions: Record<number, { width: number; height: number }> = {
      5: {
        width: 780,
        height: 880,
      },
      12: {
        width: 650,
        height: 880,
      },
      24: {
        width: 970,
        height: 880,
      },
      31: {
        width: 1024,
        height: 1000,
      },
    };

    const PAYMENT_WINDOW_DEFAULT_WIDTH = 650;
    const PAYMENT_WINDOW_DEFAULT_HEIGHT = 880;

    let windowWidth = PAYMENT_WINDOW_DEFAULT_WIDTH;
    if (windowDimensions[processorId]) {
      windowWidth = windowDimensions[processorId].width;
    }

    let windowHeight = PAYMENT_WINDOW_DEFAULT_HEIGHT;
    if (windowDimensions[processorId]) {
      windowHeight = windowDimensions[processorId].height;
    }

    const windowOptions = {
      width: windowWidth,
      height: windowHeight,
      scrollbars: 1,
      toolbars: 0,
      status: 0,
      resizable: 1,
      left: window.outerWidth / 2,
    };

    const windowOptionsString = Object
      .entries(windowOptions)
      .map(([key, value]) => `${key}=${value}`)
      .join(',');

    this.closePaymentProcessorWindow();
    this.checkoutWindow = window.open('', name, windowOptionsString);
  }
}

export const EpayServiceClass = EpayService;

export default new EpayService();
export { DISCOUNT_ELIGIBLE };
